import { apiService } from './api';
import { useAuthStore } from '../stores/authStore';

export interface Domain {
  id: string;
  name: string;
  zone: string;
  status: 'active' | 'pending' | 'inactive';
}

interface DomainsCache {
  domains: Domain[];
  lastUpdated: number;
  isLoading: boolean;
  error: string | null;
}

class DomainsCacheService {
  private cache: DomainsCache = {
    domains: [],
    lastUpdated: 0,
    isLoading: false,
    error: null,
  };

  private subscribers: ((cache: DomainsCache) => void)[] = [];
  private refreshInterval: NodeJS.Timeout | null = null;
  private lastRefreshTime = 0;
  private readonly REFRESH_COOLDOWN = 60000; // 1 хвилина

  constructor() {
    // Перевіряємо чи користувач вже авторизований (наприклад, після перезавантаження)
    const currentState = useAuthStore.getState();
    if (currentState.isAuthenticated && !this.cache.domains.length && !this.cache.isLoading) {
      this.loadDomains();
    }
    
    // Підписуємося на майбутні зміни авторизації
    this.setupAuthListener();
  }

  private setupAuthListener() {
    // Підписуємося на зміни авторизації
    useAuthStore.subscribe((state) => {
      if (state.isAuthenticated && !this.cache.domains.length && !this.cache.isLoading) {
        this.loadDomains();
      }
    });
  }

  subscribe(callback: (cache: DomainsCache) => void): () => void {
    this.subscribers.push(callback);
    
    // Відразу викликаємо callback з поточним станом
    callback(this.cache);
    
    // Повертаємо функцію для відписки
    return () => {
      const index = this.subscribers.indexOf(callback);
      if (index > -1) {
        this.subscribers.splice(index, 1);
      }
    };
  }

  private notifySubscribers() {
    this.subscribers.forEach(callback => callback(this.cache));
  }

  private async loadDomains() {
    if (this.cache.isLoading) return;

    this.cache.isLoading = true;
    this.cache.error = null;
    this.notifySubscribers();

    try {
      const zonesData = await apiService.getCloudflareZones();
      const domains = zonesData.map(zone => ({
        id: zone.id,
        name: zone.name,
        zone: zone.name,
        status: zone.status as 'active' | 'pending' | 'inactive'
      }));

      this.cache = {
        domains,
        lastUpdated: Date.now(),
        isLoading: false,
        error: null,
      };
      
      console.log(`✅ Domains loaded: ${domains.length} domains`);
    } catch (error) {
      this.cache.error = error instanceof Error ? error.message : 'Невідома помилка';
      this.cache.isLoading = false;
      
      console.error('❌ Error loading domains:', error);
    }

    this.notifySubscribers();
  }

  async refresh(): Promise<boolean> {
    const now = Date.now();
    if (now - this.lastRefreshTime < this.REFRESH_COOLDOWN) {
      const remainingTime = Math.ceil((this.REFRESH_COOLDOWN - (now - this.lastRefreshTime)) / 1000);
      throw new Error(`Зачекайте ${remainingTime} секунд перед наступним оновленням`);
    }

    this.lastRefreshTime = now;
    
    // Використовуємо refresh API для примусового оновлення
    this.cache.isLoading = true;
    this.cache.error = null;
    this.notifySubscribers();

    try {
      const zonesData = await apiService.refreshCloudflareZones();
      const domains = zonesData.map(zone => ({
        id: zone.id,
        name: zone.name,
        zone: zone.name,
        status: zone.status as 'active' | 'pending' | 'inactive'
      }));

      this.cache = {
        domains,
        lastUpdated: Date.now(),
        isLoading: false,
        error: null,
      };
      
      console.log(`✅ Domains refreshed: ${domains.length} domains`);
      this.notifySubscribers();
      return true;
    } catch (error) {
      this.cache.error = error instanceof Error ? error.message : 'Невідома помилка';
      this.cache.isLoading = false;
      
      console.error('❌ Error refreshing domains:', error);
      this.notifySubscribers();
      throw error;
    }
  }

  getDomains(): Domain[] {
    return this.cache.domains;
  }

  getCache(): DomainsCache {
    return this.cache;
  }

  isLoading(): boolean {
    return this.cache.isLoading;
  }

  getError(): string | null {
    return this.cache.error;
  }

  canRefresh(): boolean {
    const now = Date.now();
    return now - this.lastRefreshTime >= this.REFRESH_COOLDOWN;
  }

  getRefreshCooldownRemaining(): number {
    const now = Date.now();
    const remaining = this.REFRESH_COOLDOWN - (now - this.lastRefreshTime);
    return Math.max(0, remaining);
  }

  destroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
    this.subscribers = [];
  }
}

export const domainsCacheService = new DomainsCacheService();